import { useState, useCallback, useRef, useEffect } from 'react';
import { useStaleData } from '@/hooks/useStaleData';
import { useAuth } from '@/context/AuthContext';
import PageHeader from '@/components/PageHeader';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '@/App';
import { Pin, Mic, MicOff, Plus, X, Trash2, Copy, Check, Calendar } from 'lucide-react';

const TAG_OPTS = [
  { key: 'financial', label: '💰 Financial', color: '#10b981' },
  { key: 'reminder',  label: '⏰ Reminder',  color: '#f97316' },
  { key: 'idea',      label: '💡 Idea',       color: '#8b5cf6' },
];

const TAG_COLOR = { financial: '#10b981', reminder: '#f97316', idea: '#8b5cf6' };

function timeAgo(iso) {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  const h = Math.floor(diff / 3600000);
  const d = Math.floor(diff / 86400000);
  if (m < 1)  return 'Just now';
  if (m < 60) return `${m}m ago`;
  if (h < 24) return `${h}h ago`;
  if (d < 7)  return `${d}d ago`;
  return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
}

export default function QuickNotesPage() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [showAdd, setShowAdd]   = useState(false);
  const [text, setText]         = useState('');
  const [tag, setTag]           = useState('');
  const [saving, setSaving]     = useState(false);
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);

  const fetchNotes = useCallback(async () => {
    const res = await axios.get(`${API}/notes`, { headers });
    return res.data || [];
  }, [token]);

  const { data: notes, reload } = useStaleData('bm_notes_cache', fetchNotes, { fallback: [] });

  // Web Speech API
  const startVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error('Voice input not supported in this browser'); return; }
    const rec = new SR();
    rec.lang = 'en-IN';
    rec.continuous = false;
    rec.interimResults = true;
    rec.onresult = (e) => {
      const transcript = Array.from(e.results).map(r => r[0].transcript).join('');
      setText(prev => prev ? prev + ' ' + transcript : transcript);
    };
    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);
    recognitionRef.current = rec;
    rec.start();
    setListening(true);
  };

  const stopVoice = () => {
    recognitionRef.current?.stop();
    setListening(false);
  };

  const handleSave = async () => {
    if (!text.trim()) { toast.error('Write something first'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/notes`, { text: text.trim(), tag }, { headers });
      setText(''); setTag(''); setShowAdd(false);
      reload();
      toast.success('Note saved');
    } catch { toast.error('Failed to save note'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/notes/${id}`, { headers });
      reload();
    } catch { toast.error('Could not delete'); }
  };

  const handlePin = async (id, pinned) => {
    try {
      await axios.patch(`${API}/notes/${id}`, { pinned: !pinned }, { headers });
      reload();
    } catch {}
  };

  const addToCalendar = async (note) => {
    try {
      const today = new Date().toISOString().slice(0, 10);
      await axios.post(`${API}/calendar`, {
        title: note.text.slice(0, 80),
        date: today,
        type: 'custom',
        color: note.tag === 'reminder' ? 'orange' : note.tag === 'financial' ? 'green' : 'blue',
        notes: note.text,
      }, { headers });
      toast.success('Added to calendar!');
    } catch { toast.error('Could not add to calendar'); }
  };

  const pinned   = (notes || []).filter(n => n.pinned);
  const unpinned = (notes || []).filter(n => !n.pinned);

  // Group unpinned by date (YYYY-MM-DD)
  const byDay = {};
  unpinned.forEach(n => {
    const day = n.created_at?.slice(0, 10) || 'unknown';
    if (!byDay[day]) byDay[day] = [];
    byDay[day].push(n);
  });
  const sortedDays = Object.keys(byDay).sort((a, b) => b.localeCompare(a));

  const dayLabel = (iso) => {
    const d = new Date(iso);
    const today = new Date();
    const yesterday = new Date(); yesterday.setDate(yesterday.getDate() - 1);
    if (iso === today.toISOString().slice(0, 10)) return 'Today';
    if (iso === yesterday.toISOString().slice(0, 10)) return 'Yesterday';
    return d.toLocaleDateString('en-IN', { weekday: 'long', day: '2-digit', month: 'short' });
  };

  return (
    <div className="min-h-screen bg-stone-50 dark:bg-stone-950">
      <PageHeader title="Quick Notes" subtitle="Capture financial thoughts instantly" />

      <div className="max-w-2xl mx-auto px-4 py-5 space-y-5">

        {/* Add note card */}
        {showAdd ? (
          <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/40 rounded-2xl p-4 space-y-3 shadow-sm">
            <div className="flex items-center justify-between">
              <span className="text-sm font-bold text-amber-800 dark:text-amber-300">New Note</span>
              <button onClick={() => { setShowAdd(false); setText(''); setTag(''); }}
                className="text-stone-400 hover:text-stone-600 transition-colors">
                <X size={18} />
              </button>
            </div>
            <textarea
              autoFocus
              value={text}
              onChange={e => setText(e.target.value)}
              placeholder="What's on your mind? (e.g. 'Check SIP returns', 'Pay rent on 5th')"
              rows={4}
              className="w-full bg-white dark:bg-stone-900 border border-amber-200 dark:border-amber-800/40 rounded-xl px-3 py-2.5 text-sm text-stone-800 dark:text-stone-100 placeholder-stone-400 focus:outline-none focus:border-amber-400 resize-none"
            />
            {/* Tag row */}
            <div className="flex items-center gap-2 flex-wrap">
              {TAG_OPTS.map(t => (
                <button key={t.key} onClick={() => setTag(tag === t.key ? '' : t.key)}
                  className={`text-xs px-3 py-1.5 rounded-full font-semibold border transition-all ${tag === t.key ? 'text-white border-transparent' : 'text-stone-500 border-stone-200 dark:border-stone-700 hover:border-stone-400'}`}
                  style={tag === t.key ? { backgroundColor: TAG_COLOR[t.key], borderColor: TAG_COLOR[t.key] } : {}}>
                  {t.label}
                </button>
              ))}
              <div className="ml-auto flex items-center gap-2">
                <button
                  onClick={listening ? stopVoice : startVoice}
                  className={`p-2 rounded-xl transition-colors ${listening ? 'bg-red-100 text-red-500 dark:bg-red-950/40' : 'bg-stone-100 dark:bg-stone-800 text-stone-500 hover:text-stone-700 dark:hover:text-stone-300'}`}
                  title={listening ? 'Stop recording' : 'Voice input'}>
                  {listening ? <MicOff size={16} className="animate-pulse" /> : <Mic size={16} />}
                </button>
                <button onClick={handleSave} disabled={saving || !text.trim()}
                  className="px-4 py-2 bg-amber-500 hover:bg-amber-600 disabled:opacity-40 text-white text-sm font-bold rounded-xl transition-colors">
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <button onClick={() => setShowAdd(true)}
            className="w-full flex items-center gap-3 bg-white dark:bg-stone-900 border border-dashed border-stone-300 dark:border-stone-700 rounded-2xl px-4 py-3.5 text-stone-400 hover:border-amber-400 hover:text-amber-500 transition-all text-sm font-semibold">
            <Plus size={18} /> Add a quick note…
            <span className="ml-auto flex items-center gap-1.5 text-xs text-stone-400">
              <Mic size={12} /> Voice supported
            </span>
          </button>
        )}

        {/* Pinned */}
        {pinned.length > 0 && (
          <div>
            <p className="text-[10px] font-bold tracking-widest text-stone-400 uppercase mb-2">📌 Pinned</p>
            <div className="space-y-2">
              {pinned.map(n => <NoteCard key={n.id} note={n} onDelete={handleDelete} onPin={handlePin} onCalendar={addToCalendar} />)}
            </div>
          </div>
        )}

        {/* Day-wise grouped notes */}
        {sortedDays.length > 0 ? (
          <div className="space-y-5">
            {sortedDays.map(day => (
              <div key={day}>
                <p className="text-[11px] font-bold tracking-widest text-stone-400 uppercase mb-2 flex items-center gap-2">
                  <span className="w-4 h-px bg-stone-300 dark:bg-stone-700 inline-block" />
                  {dayLabel(day)}
                  <span className="w-full h-px bg-stone-200 dark:bg-stone-800 inline-block" />
                </p>
                <div className="space-y-2">
                  {byDay[day].map(n => (
                    <NoteCard key={n.id} note={n} onDelete={handleDelete} onPin={handlePin} onCalendar={addToCalendar} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : pinned.length === 0 && (
          <div className="text-center py-16 space-y-2">
            <span className="text-5xl">📝</span>
            <p className="font-bold text-stone-700 dark:text-stone-300">No notes yet</p>
            <p className="text-sm text-stone-400">Tap "Add a quick note" or use voice to capture something</p>
          </div>
        )}
      </div>
    </div>
  );
}

function NoteCard({ note, onDelete, onPin, onCalendar }) {
  const [copied, setCopied] = useState(false);
  const tagColor = TAG_COLOR[note.tag] || '#78716c';

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(note.text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { toast.error('Copy failed'); }
  };

  return (
    <div className="group bg-amber-50 dark:bg-amber-950/20 border border-amber-200/60 dark:border-amber-800/30 rounded-2xl p-4 relative hover:shadow-sm transition-shadow">
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm text-stone-800 dark:text-stone-200 leading-relaxed flex-1 whitespace-pre-wrap">{note.text}</p>
        <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
          <button onClick={handleCopy}
            className="p-1.5 rounded-lg text-stone-400 hover:text-stone-600 hover:bg-stone-100 dark:hover:bg-stone-800 transition-colors"
            title="Copy text">
            {copied ? <Check size={13} className="text-emerald-500" /> : <Copy size={13} />}
          </button>
          <button onClick={() => onCalendar(note)}
            className="p-1.5 rounded-lg text-stone-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-950/30 transition-colors"
            title="Add to calendar">
            <Calendar size={13} />
          </button>
          <button onClick={() => onPin(note.id, note.pinned)}
            className={`p-1.5 rounded-lg transition-colors ${note.pinned ? 'text-amber-500 bg-amber-100 dark:bg-amber-900/40' : 'text-stone-400 hover:text-stone-600 hover:bg-stone-100 dark:hover:bg-stone-800'}`}
            title={note.pinned ? 'Unpin' : 'Pin'}>
            <Pin size={13} />
          </button>
          <button onClick={() => onDelete(note.id)}
            className="p-1.5 rounded-lg text-stone-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors">
            <Trash2 size={13} />
          </button>
        </div>
      </div>
      <div className="flex items-center gap-2 mt-2.5">
        <span className="text-[11px] text-stone-400">{timeAgo(note.created_at)}</span>
        {note.tag && (
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full"
            style={{ backgroundColor: tagColor + '18', color: tagColor }}>
            {TAG_OPTS.find(t => t.key === note.tag)?.label || note.tag}
          </span>
        )}
      </div>
    </div>
  );
}
